/**
 * @module provider
 * @package @geenius/adapters/react-native
 * @description Implements the React Native adapter provider and context access
 * helpers with native-safe default adapters.
 */

"use client";

import {
  ADAPTER_DOMAINS,
  AdapterContextError,
  type AdapterDomain,
  AdapterError,
  type AdapterStatusInfo,
  type AdminAdapter,
  type AiAdapter,
  type AuthAdapter,
  type DbAdapter,
  DOMAIN_LABELS,
  type FileStorageAdapter,
  getDomainConfig,
  isAdaptersConfigured,
  type LoggerAdapter,
  type PaymentsAdapter,
} from "@geenius/adapters";
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  createReactNativeDefaultAdapterSet,
  REACT_NATIVE_DEFAULT_PROVIDER_BY_DOMAIN,
  type ReactNativeAsyncStorage,
} from "../lib/defaultAdapters";

type AnyAdapter =
  | DbAdapter
  | AuthAdapter
  | PaymentsAdapter
  | AiAdapter
  | FileStorageAdapter
  | AdminAdapter
  | LoggerAdapter;

type AdapterForDomain<D extends AdapterDomain> = D extends "db"
  ? DbAdapter
  : D extends "auth"
    ? AuthAdapter
    : D extends "payments"
      ? PaymentsAdapter
      : D extends "ai"
        ? AiAdapter
        : D extends "storage"
          ? FileStorageAdapter
          : D extends "admin"
            ? AdminAdapter
            : D extends "logger"
              ? LoggerAdapter
              : never;

type AdapterByDomain = {
  [D in AdapterDomain]: AdapterForDomain<D>;
};

type ResolvedAdapterSet = AdapterByDomain & {
  fileStorage: FileStorageAdapter;
};

/**
 * Adapter instances that may be supplied to the native provider.
 *
 * @property admin Optional admin adapter override.
 * @property ai Optional AI adapter override.
 * @property auth Optional auth adapter override.
 * @property db Optional DB adapter override.
 * @property fileStorage Optional legacy alias for the storage adapter.
 * @property logger Optional logger adapter override.
 * @property payments Optional payments adapter override.
 * @property storage Optional file storage adapter override.
 */
export interface AdapterSet extends Partial<Record<AdapterDomain, AnyAdapter>> {
  admin?: AdminAdapter;
  ai?: AiAdapter;
  auth?: AuthAdapter;
  db?: DbAdapter;
  fileStorage?: FileStorageAdapter;
  logger?: LoggerAdapter;
  payments?: PaymentsAdapter;
  storage?: FileStorageAdapter;
}

/**
 * Registry shape accepted by {@link AdapterProvider} for adapter overrides.
 *
 * @example
 * ```tsx
 * <AdapterProvider registry={{ db: memoryDb }} />
 * ```
 */
export type AdapterRegistry = AdapterSet;

/**
 * Runtime context value exposed by {@link useAdapterContext}.
 *
 * @property adapters Fully resolved adapter instances.
 * @property getAdapter Typed adapter lookup helper by domain.
 * @property isLoading Whether provider health checks are still running.
 * @property isReady Readiness helper for a single domain.
 * @property statuses Status metadata keyed by adapter domain.
 */
export interface AdapterContextValue {
  adapters: ResolvedAdapterSet;
  getAdapter: <K extends AdapterDomain>(domain: K) => AdapterByDomain[K];
  isLoading: boolean;
  isReady: (domain: AdapterDomain) => boolean;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
}

export type AdapterHealthProbeMap = Partial<{
  [D in AdapterDomain]: (
    adapter: AdapterByDomain[D],
    adapters: ResolvedAdapterSet,
  ) => Promise<void> | void;
}>;

/**
 * Props for the native adapter provider.
 *
 * @property adapters Optional bundle of adapter overrides.
 * @property asyncStorage Async storage implementation used by local native defaults.
 * @property children React subtree receiving the adapter context.
 * @property healthCheck Enables startup health checks for configured adapters.
 * @property healthProbes Optional domain-specific health probes supplied by the host app.
 * @property namespace Storage namespace used by default local adapters.
 * @property onError Optional callback for health-check adapter errors.
 * @property registry Optional registry alias for adapter overrides.
 * @property statusOverrides Optional status metadata overrides for stories/tests.
 */
export interface AdapterProviderProps extends Partial<AdapterSet> {
  adapters?: AdapterSet;
  asyncStorage?: ReactNativeAsyncStorage;
  children: ReactNode;
  healthCheck?: boolean;
  healthProbes?: AdapterHealthProbeMap;
  namespace?: string;
  onError?: (error: AdapterError) => void;
  registry?: Partial<AdapterRegistry>;
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>;
}

type AdapterResolutionProps = Pick<
  AdapterProviderProps,
  | "adapters"
  | "registry"
  | "db"
  | "auth"
  | "payments"
  | "ai"
  | "storage"
  | "fileStorage"
  | "admin"
  | "logger"
>;

const AdapterContext = createContext<AdapterContextValue | null>(null);

function resolveAdapterSet(
  props: AdapterResolutionProps,
  defaults: ResolvedAdapterSet,
): ResolvedAdapterSet {
  const provided = {
    ...(props.registry ?? {}),
    ...(props.adapters ?? {}),
  };
  const fileStorage =
    props.fileStorage ??
    props.storage ??
    provided.fileStorage ??
    provided.storage ??
    defaults.storage;

  return {
    db: props.db ?? provided.db ?? defaults.db,
    auth: props.auth ?? provided.auth ?? defaults.auth,
    payments: props.payments ?? provided.payments ?? defaults.payments,
    ai: props.ai ?? provided.ai ?? defaults.ai,
    storage: fileStorage,
    fileStorage,
    admin: props.admin ?? provided.admin ?? defaults.admin,
    logger: props.logger ?? provided.logger ?? defaults.logger,
  };
}

function getInitialProviderName(
  domain: AdapterDomain,
  adapters: ResolvedAdapterSet,
  defaultProviderName: string,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): string {
  const overrideProvider = statusOverrides?.[domain]?.provider;
  if (overrideProvider !== undefined) {
    return overrideProvider;
  }

  if (!adapters[domain]) {
    return "none";
  }

  if (!isAdaptersConfigured()) {
    return domain === "logger" ? "console" : defaultProviderName;
  }

  return (
    getDomainConfig(domain)?.provider ??
    REACT_NATIVE_DEFAULT_PROVIDER_BY_DOMAIN[domain]
  );
}

function createInitialStatuses(
  adapters: ResolvedAdapterSet,
  defaultProviderName: string,
  healthCheck: boolean,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): Record<AdapterDomain, AdapterStatusInfo> {
  const initial = {} as Record<AdapterDomain, AdapterStatusInfo>;

  for (const domain of ADAPTER_DOMAINS) {
    const adapter = adapters[domain];
    initial[domain] = {
      domain,
      provider: getInitialProviderName(
        domain,
        adapters,
        defaultProviderName,
        statusOverrides,
      ),
      status: adapter
        ? healthCheck
          ? "initializing"
          : "connected"
        : "disconnected",
      lastCheckedAt: Date.now(),
      ...statusOverrides?.[domain],
    };
  }

  return initial;
}

function toAdapterError(domain: AdapterDomain, error: unknown): AdapterError {
  if (error instanceof AdapterError) {
    return error;
  }

  const message =
    error instanceof Error ? error.message : "Health check failed";
  return new AdapterError(message, domain, "SERVICE_ERROR", error);
}

async function probeAdapterDomain(
  domain: AdapterDomain,
  adapters: ResolvedAdapterSet,
  healthProbes?: AdapterHealthProbeMap,
): Promise<{ connected: true } | { connected: false; error: string }> {
  const customProbe = healthProbes?.[domain];
  if (customProbe) {
    await customProbe(adapters[domain] as never, adapters);
    return { connected: true };
  }

  switch (domain) {
    case "auth":
      await adapters.auth.getSession();
      return { connected: true };
    case "payments":
      await adapters.payments.getPlans();
      return { connected: true };
    case "admin":
      await adapters.admin.getMetrics();
      return { connected: true };
    case "logger":
      await adapters.logger.child({ healthCheck: true });
      return { connected: true };
    case "db":
      await adapters.db.count("__geenius_adapter_healthcheck");
      return { connected: true };
    case "storage":
      await adapters.storage.list("__geenius_adapter_healthcheck");
      return { connected: true };
    case "ai":
      await adapters.ai.complete("geenius adapter health check");
      return { connected: true };
  }

  return {
    connected: false,
    error: `No health probe is registered for ${domain}.`,
  };
}

/**
 * Provides native adapter instances, status metadata, and readiness helpers.
 *
 * @param props Provider props including adapter overrides and local defaults.
 * @returns A React context provider for React Native adapter consumers.
 * @example
 * ```tsx
 * <AdapterProvider healthCheck asyncStorage={storage}>
 *   <AdaptersPage />
 * </AdapterProvider>
 * ```
 */
export function AdapterProvider(props: AdapterProviderProps): JSX.Element {
  const {
    children,
    healthCheck = false,
    statusOverrides,
    adapters: providedAdapters,
    registry,
    db,
    auth,
    payments,
    ai,
    storage,
    fileStorage,
    admin,
    logger,
    onError,
    healthProbes,
    asyncStorage,
    namespace,
  } = props;
  const defaultBundle = useMemo(
    () => createReactNativeDefaultAdapterSet({ asyncStorage, namespace }),
    [asyncStorage, namespace],
  );
  const adapters = useMemo(
    () =>
      resolveAdapterSet(
        {
          adapters: providedAdapters,
          registry,
          db,
          auth,
          payments,
          ai,
          storage,
          fileStorage,
          admin,
          logger,
        },
        defaultBundle.adapters,
      ),
    [
      providedAdapters,
      registry,
      db,
      auth,
      payments,
      ai,
      storage,
      fileStorage,
      admin,
      logger,
      defaultBundle,
    ],
  );
  const [isLoading, setIsLoading] = useState(healthCheck);
  const [statuses, setStatuses] = useState<
    Record<AdapterDomain, AdapterStatusInfo>
  >(() =>
    createInitialStatuses(
      adapters,
      defaultBundle.providerName,
      healthCheck,
      statusOverrides,
    ),
  );

  useEffect(() => {
    setStatuses(
      createInitialStatuses(
        adapters,
        defaultBundle.providerName,
        healthCheck,
        statusOverrides,
      ),
    );
  }, [adapters, defaultBundle.providerName, healthCheck, statusOverrides]);

  useEffect(() => {
    if (!healthCheck) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const checks = ADAPTER_DOMAINS.map(async (domain: AdapterDomain) => {
      const adapter = adapters[domain];
      if (!adapter) return;

      try {
        setStatuses((previous) => ({
          ...previous,
          [domain]: {
            ...previous[domain],
            status: "initializing",
            error: undefined,
          },
        }));

        const result = await probeAdapterDomain(domain, adapters, healthProbes);
        if (!result.connected) {
          setStatuses((previous) => ({
            ...previous,
            [domain]: {
              ...previous[domain],
              status: "disconnected",
              error: result.error,
              lastCheckedAt: Date.now(),
            },
          }));
          return;
        }

        if (!cancelled) {
          setStatuses((previous) => ({
            ...previous,
            [domain]: {
              ...previous[domain],
              status: "connected",
              error: undefined,
              lastCheckedAt: Date.now(),
            },
          }));
        }
      } catch (error) {
        const adapterError = toAdapterError(domain, error);
        onError?.(adapterError);

        if (!cancelled) {
          setStatuses((previous) => ({
            ...previous,
            [domain]: {
              ...previous[domain],
              status: "error",
              error: adapterError.message,
              lastCheckedAt: Date.now(),
            },
          }));
        }
      }
    });

    Promise.allSettled(checks).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [adapters, healthCheck, healthProbes, onError]);

  const getAdapter = useCallback(
    <K extends AdapterDomain>(domain: K): AdapterByDomain[K] => {
      const adapter = adapters[domain];
      if (!adapter) {
        throw new AdapterContextError(
          `${DOMAIN_LABELS[domain] ?? domain} adapter not configured in <AdapterProvider>`,
          "ADAPTER_NOT_CONFIGURED",
        );
      }
      return adapter;
    },
    [adapters],
  );

  const isReady = useCallback(
    (domain: AdapterDomain) =>
      !!adapters[domain] && statuses[domain]?.status === "connected",
    [adapters, statuses],
  );

  const value = useMemo<AdapterContextValue>(
    () => ({
      adapters,
      statuses,
      getAdapter,
      isReady,
      isLoading,
    }),
    [adapters, statuses, getAdapter, isReady, isLoading],
  );

  return (
    <AdapterContext.Provider value={value}>{children}</AdapterContext.Provider>
  );
}

/**
 * Alias for {@link AdapterProvider} retained for naming parity with web variants.
 *
 * @example
 * ```tsx
 * <AdaptersProvider><App /></AdaptersProvider>
 * ```
 */
export const AdaptersProvider = AdapterProvider;

/**
 * Reads the native adapter context from the nearest provider.
 *
 * @returns The resolved adapter context value.
 * @throws AdapterContextError when rendered outside an {@link AdapterProvider}.
 * @example
 * ```tsx
 * const { statuses, getAdapter } = useAdapterContext();
 * ```
 */
export function useAdapterContext(): AdapterContextValue {
  const context = useContext(AdapterContext);
  if (!context) {
    throw new AdapterContextError(
      "useAdapterContext must be used within <AdapterProvider>",
      "MISSING_CONTEXT",
    );
  }
  return context;
}
